import { registerBlock } from "../../registry";
import { makeId } from "../../helpers";
import type { ContentBlock } from "../../types";

const embryoTransfer: ContentBlock<"procedure"> = {
  id: makeId("proc-embryo-transfer"),
  kind: "procedure",
  tags: ["ivf", "her", "during-cycle"],
  title: "Embryo Transfer",
  subtitle:
    "Gentle procedure to place an embryo into the uterus, no anesthesia needed",
  body: {
    procedureName: "Embryo Transfer",
    category: "transfer",
    description:
      "A gentle procedure to place an embryo into the uterus. No anesthesia needed, similar to a Pap smear.",
    howItWorks:
      "Using ultrasound guidance with a full bladder, the doctor threads a thin, flexible catheter through the cervix into the uterus. The embryologist loads the embryo(s) into the catheter in the lab, then hands it to the doctor who carefully deposits the embryo at the optimal position in the uterine lining. The catheter is checked under microscope to confirm the embryo was released.",
    duration: "10-15 minutes",
    anesthesia: "none",
    recoveryTime:
      "Rest for 10-15 minutes at clinic, then resume normal light activities",
    costRange: { low: 1000, high: 4000 },
    currency: "USD",
    whatToExpect: [
      "Arrive with a comfortably full bladder",
      "No sedation, you'll be awake and can watch on screen",
      "Mild pressure or cramping (like a Pap smear)",
      "The entire procedure is very quick",
      "You can resume light activities immediately after",
      "No bed rest required, studies show it doesn't improve outcomes",
      "Continue progesterone and estrogen support exactly as directed through the two-week wait and, if pregnant, into early pregnancy unless your clinic changes the plan",
    ],
    prepInstructions: [
      "Drink 16-32 oz of water 1 hour before to fill your bladder",
      "Take any prescribed medications (Valium, progesterone) as directed",
      "Arrive 15-30 minutes early",
      "Wear comfortable clothing",
      "Confirm how long to continue progesterone support and what to do if you spot or miss a dose",
    ],
    results: [
      {
        label: "Procedure confirmation",
        detail:
          "The embryology team checks the catheter after transfer to confirm the embryo was released.",
      },
      {
        label: "Pregnancy test timing",
        detail:
          "The real result is the beta hCG blood test, often scheduled about 9-14 days after transfer depending on embryo stage and clinic protocol.",
      },
      {
        label: "If positive",
        detail:
          "Many clinics repeat beta hCG about 48 hours later and continue luteal support while early pregnancy monitoring begins.",
      },
      {
        label: "Home tests",
        detail:
          "Home pregnancy tests can be misleading if taken too early. Follow your clinic's testing date even if you test at home.",
      },
    ],
    risks: [
      "Mild cramping",
      "Very rare: infection",
      "Light spotting after procedure",
    ],
    forWhom: "her",
    frBody: {
      procedureName: "Transfert d'embryon",
      description:
        "Geste doux pour déposer un embryon dans l'utérus. Pas d'anesthésie nécessaire, comparable à un frottis.",
      howItWorks:
        "Sous guidage échographique vessie pleine, le médecin introduit un fin cathéter souple à travers le col jusque dans l'utérus. L'embryologiste charge le ou les embryons dans le cathéter au laboratoire, puis le remet au médecin qui dépose l'embryon à l'endroit optimal dans la muqueuse utérine. Le cathéter est ensuite vérifié au microscope pour confirmer le dépôt.",
      duration: "10 à 15 minutes",
      recoveryTime:
        "Repos de 10 à 15 minutes au centre, puis reprise des activités légères normales",
      whatToExpect: [
        "Arrivez avec la vessie confortablement pleine",
        "Pas de sédation : vous êtes éveillée et pouvez suivre à l'écran",
        "Légère pression ou crampe (comme un frottis)",
        "Le geste est très rapide",
        "Vous pouvez reprendre une activité légère immédiatement après",
        "Pas de repos au lit nécessaire : les études montrent que cela n'améliore pas les résultats",
        "Continuez la progestérone et l'oestrogène exactement comme indiqué pendant l'attente de deux semaines et, en cas de grossesse, au début de la grossesse sauf consigne contraire du centre",
      ],
      prepInstructions: [
        "Buvez environ 500 mL à 1 L d'eau 1 heure avant pour remplir la vessie",
        "Prenez les médicaments prescrits (Valium, progestérone) selon les indications",
        "Arrivez 15 à 30 minutes en avance",
        "Portez des vêtements confortables",
        "Confirmez combien de temps continuer la progestérone et quoi faire en cas de spotting ou d'oubli de dose",
      ],
      results: [
        {
          label: "Confirmation du geste",
          detail:
            "L'équipe d'embryologie vérifie le cathéter après le transfert pour confirmer que l'embryon a bien été déposé.",
        },
        {
          label: "Test de grossesse",
          detail:
            "Le vrai résultat est le dosage sanguin de bêta-hCG, souvent prévu environ 9 à 14 jours après le transfert selon le stade de l'embryon et le protocole du centre.",
        },
        {
          label: "Si positif",
          detail:
            "Beaucoup de centres répètent la bêta-hCG environ 48 heures plus tard et poursuivent le soutien lutéal pendant le début du suivi de grossesse.",
        },
        {
          label: "Tests urinaires",
          detail:
            "Les tests de grossesse urinaires peuvent être trompeurs s'ils sont faits trop tôt. Suivez la date de test donnée par votre centre, même si vous testez à domicile.",
        },
      ],
      risks: [
        "Crampe légère",
        "Très rare : infection",
        "Léger spotting après le geste",
      ],
    },
  },
  sources: [
    {
      title: "Performing the embryo transfer: a guideline",
      authors: "Practice Committee of the ASRM",
      journal: "Fertility and Sterility",
      year: 2017,
      url: "https://pubmed.ncbi.nlm.nih.gov/28366416/",
      evidenceLevel: "guideline",
    },
  ],
};

registerBlock(embryoTransfer);
